/**
 * V70G7B — 로그인 사용자에게 활성 팝업 공지를 우선순위 순서대로 한 개씩 표시한다.
 * V70G7D — 목록이 실시간 갱신되어도 열려 있는 팝업은 유지하고, 신규 팝업은 대기열에 반영한다.
 *          표시 종료(displayUntil)가 지나면 기록 없이 화면에서 제거한다.
 * V70G8B — 게임대회 대시보드 공지 팝업을 같은 대기열에 합쳐 표시 (모달은 항상 1개).
 *          게임대회 팝업 노출 범위는 GAME_CENTER_POPUP_SCOPE 로 결정 (서버도 게임대회 접근 권한 검증).
 * uid 가 바뀌면 key 로 재마운트되어 이전 사용자 상태를 버린다 (AppShell 참고).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupAnnouncementModal } from './PopupAnnouncementModal';
import {
  PopupRecordAction,
  detailKeyFromLocation,
  markSessionHidden,
  pathnameOf,
  popupDetailKeyOf,
  popupDetailPath,
  popupSourceLabel,
  recordPopupAction,
  usePopupFeed,
} from '../hooks/usePopupAnnouncements';
import {
  handledKey,
  kstDateOf,
  mergePopupFeeds,
  nextExpiryMs,
  pickCurrent,
  popupKey,
  popupProgress,
  visiblePopups,
} from '../hooks/popupSync';
import {
  cancelPendingDetailHold,
  holdUntilDetailOpens,
  isPopupHeld,
  pendingDetailHold,
  usePopupHeld,
} from '../hooks/popupHold';

interface Props {
  uid: string;
}

/**
 * 게임대회 공지 팝업 노출 범위 (V70G8B 확정: erp-wide)
 *   'tournament-routes': /tournament/* 화면에 있을 때만
 *   'erp-wide'         : 모든 ERP 화면 (게임대회 접근 권한자만 — 서버가 권한 없는 사용자에게 빈 목록 반환,
 *                        게임대회 설정 변경 시 신호로 재조회되어 권한을 잃은 사용자의 팝업도 제거)
 */
export type GameCenterPopupScope = 'tournament-routes' | 'erp-wide';
export const GAME_CENTER_POPUP_SCOPE: GameCenterPopupScope = 'erp-wide';

export function isGameCenterPopupEnabled(scope: GameCenterPopupScope, pathname: string): boolean {
  if (scope === 'erp-wide') return true;
  return pathname === '/tournament' || pathname.startsWith('/tournament/');
}

const MAX_EXPIRY_TIMER_MS = 6 * 60 * 60 * 1000;

export function GlobalPopupManager({ uid }: Props) {
  const navigate = useNavigate();
  const { pathname, search, key: locationKey } = useLocation();
  // V70G8E: 상세 진입 주소(딥링크 포함)면 그 상세가 실제로 열려 보류를 넘겨받을 때까지 보류.
  //   (V70G8C 는 주소가 바뀌는 순간 보류를 풀어, 게임대회 대시보드의 접근 권한 조회·페이지 마운트·
  //    상세 모달 마운트 사이 공백에 다음 팝업이 뜨고 shown 으로 기록됐다.)
  //   다른 화면으로 이동하면 대기 보류 해제. 레이아웃 단계에서 처리해 공백 렌더가 화면/기록에 남지 않게 한다.
  const deepLinkKey = detailKeyFromLocation(pathname, search);
  const processedLocationRef = useRef<string | null>(null);
  const locationId = `${locationKey ?? ''}|${pathname}|${search ?? ''}`;
  useLayoutEffect(() => {
    processedLocationRef.current = locationId;
    if (deepLinkKey) {
      holdUntilDetailOpens(deepLinkKey, pathname);
      return;
    }
    const pending = pendingDetailHold();
    if (pending && pending.pathname !== pathname) cancelPendingDetailHold();
  }, [locationId, deepLinkKey, pathname]);
  useEffect(() => cancelPendingDetailHold, []);
  // 레이아웃 단계 처리 전 첫 렌더에서도 딥링크면 표시하지 않음
  const awaitingDeepLink = deepLinkKey !== null && processedLocationRef.current !== locationId;
  const gameCenterEnabled = isGameCenterPopupEnabled(GAME_CENTER_POPUP_SCOPE, pathname);
  const erpPopups = usePopupFeed(uid, 'erp', true);
  const gameCenterPopups = usePopupFeed(uid, 'gameCenter', gameCenterEnabled);
  const popups = useMemo(() => mergePopupFeeds(erpPopups, gameCenterPopups), [erpPopups, gameCenterPopups]);
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set());
  const [clock, setClock] = useState(() => Date.now());
  const currentIdRef = useRef<string | null>(null);
  const shownRef = useRef<Set<string>>(new Set());

  // 목록이 바뀔 때마다 현재 시각 기준으로 다시 계산
  useEffect(() => {
    setClock(Date.now());
  }, [popups]);

  const visible = useMemo(() => visiblePopups(popups, handled, clock), [popups, handled, clock]);
  // V70G8F: 이번 연속 표시에서 사용자가 처리한 팝업 수 (진행 위치 계산용). 대기열이 비면 새 연속 표시로 초기화.
  const [completedInRun, setCompletedInRun] = useState(0);
  useEffect(() => {
    if (visible.length === 0 && completedInRun !== 0) setCompletedInRun(0);
  }, [visible.length, completedInRun]);
  // V70G8C: 공지 상세(일반 공지 상세 화면 / 게임대회 공지 상세 모달)를 보는 동안 표시 보류.
  //   대기열·처리 상태는 그대로 두고, 상세를 닫으면 이어서 표시한다.
  const heldState = usePopupHeld();
  const held = heldState || isPopupHeld() || awaitingDeepLink;
  const current = held ? null : pickCurrent(visible, currentIdRef.current);
  if (!held) currentIdRef.current = current ? popupKey(current) : null;

  // 표시 종료 시각에 맞춰 화면 갱신 (조회 없음)
  useEffect(() => {
    const next = nextExpiryMs(visible, clock);
    if (next === null) return undefined;
    const t = window.setTimeout(
      () => setClock(Date.now()),
      Math.min(Math.max(0, next - Date.now()) + 1_000, MAX_EXPIRY_TIMER_MS),
    );
    return () => window.clearTimeout(t);
  }, [visible, clock]);

  useEffect(() => {
    if (!current) return;
    const key = handledKey(current, kstDateOf(Date.now()));
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    void recordPopupAction(current, 'shown');
  }, [current]);

  const finish = useCallback(
    (action: PopupRecordAction) => {
      if (!current) return;
      if (current.popupDismissPolicy === 'session') markSessionHidden(uid, current);
      void recordPopupAction(current, action);
      const key = handledKey(current, kstDateOf(Date.now()));
      currentIdRef.current = null;
      setCompletedInRun((n) => n + 1);
      setHandled((prev) => new Set(prev).add(key));
      setClock(Date.now());
    },
    [current, uid],
  );

  const handleClose = useCallback(() => finish('dismiss'), [finish]);
  const handleHideForDay = useCallback(() => finish('hideForDay'), [finish]);
  const handleViewDetail = useCallback(() => {
    if (!current) return;
    const path = popupDetailPath(current);
    // V70G8E: 클릭 순간부터 그 상세가 열릴 때까지 보류 (이동·권한 조회·모달 렌더 지연 포함)
    holdUntilDetailOpens(popupDetailKeyOf(current), pathnameOf(path));
    finish('dismiss');
    navigate(path);
  }, [current, finish, navigate]);
  // V70G8H: 본문 내부 링크 클릭 — '상세 보기'와 같이 이 팝업은 처리(dismiss)하고 이동.
  //   공지 상세 주소면 위 딥링크 보류(V70G8E)가 그대로 적용되고, 권한은 대상 라우트 가드가 검사한다.
  const handleInternalLink = useCallback((path: string) => {
    if (!current) return;
    finish('dismiss');
    navigate(path);
  }, [current, finish, navigate]);

  if (!current) return null;

  return (
    <PopupAnnouncementModal
      popup={current}
      sourceLabel={popupSourceLabel(current)}
      progress={popupProgress(completedInRun, visible.length)}
      onClose={handleClose}
      onHideForDay={handleHideForDay}
      onViewDetail={handleViewDetail}
      onInternalLink={handleInternalLink}
    />
  );
}
